import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Edit2,
  Trash2,
  Calendar,
  Copy,
  Plus,
  Receipt,
  ToggleLeft,
  ToggleRight,
  Award,
  ChevronLeft,
  FileSpreadsheet,
  Bell,
  Mail,
  MessageSquare,
  RefreshCw,
  Loader2,
  Settings,
  Building2,
  Archive,
  RotateCcw,
  Users,
  Star,
  AlertCircle,
} from 'lucide-react';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import {
  useTuitionGrids,
  useDashboardSummary,
  useSyncFilieres,
  useTuitionGridSettings,
  useSaveTuitionGridSettings,
  useTuitionGridsGrouped,
  useCampusInfo,
} from '../../hooks/useTuitionGrids';
import { useAncillaryFees } from '../../hooks/useAncillaryFees';
import { useDiscounts } from '../../hooks/useDiscounts';
import {
  usePaymentSchedules,
  useSetDefaultPaymentSchedule,
  useDuplicatePaymentSchedule,
  useArchivePaymentSchedule,
  useRestorePaymentSchedule,
  useDeletePaymentSchedule,
} from '../../hooks/usePaymentSchedules';
import {
  useReminderConfigs,
  useUpdateReminder,
  useDeleteReminder,
  useReminderHistory,
} from '../../hooks/useReminders';
import type { ReminderConfig } from '@/services/reminderService';
import PaymentScheduleAssignModal from '../../components/tuition/PaymentScheduleAssignModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import type { TuitionGridWithRelations, TuitionGridQuery, TuitionGridSettings, TypeFormation, PaymentScheduleTemplate } from '@/types/tuition.types';
import type { FiliereWithOptions } from '@/types/filiere.types';
import { formatCurrency } from '@/services/tuitionService';
import { fetchFilieresGrouped } from '@/services/filieresService';
import TuitionGridFormModal from '../../components/tuition/TuitionGridFormModal';
import TuitionGridArchiveModal from '../../components/tuition/TuitionGridArchiveModal';
import TuitionGridSettingsModal from '../../components/tuition/TuitionGridSettingsModal';
import TuitionGridGroupedTable from '../../components/tuition/TuitionGridGroupedTable';
import PaymentScheduleFormModal from '../../components/tuition/PaymentScheduleFormModal';
import { AncillaryFeesPage } from './AncillaryFeesPage';
import DiscountsPage from './DiscountsPage';
import { ReminderFormModal } from '../../components/reminders/ReminderFormModal';

// PaymentScheduleTemplate type is now imported from tuition.types.ts
// AncillaryFee type is now imported from tuition.types.ts and used in AncillaryFeesPage
// DiscountConfig type is now in tuition.types.ts and used in DiscountsPage
// ReminderConfig type is now imported from reminderService.ts

type ActiveSection = 'dashboard' | 'grille' | 'echeanciers' | 'annexes' | 'remises' | 'relances';

// Mock templates removed - now using real API via usePaymentSchedules hook
// Mock fees removed - now using real API in AncillaryFeesPage
// Mock discounts removed - now using real API in DiscountsPage
// Mock reminders removed - now using real API via useReminderConfigs hook

// Helper labels
const scheduleTypeLabels: Record<string, string> = {
  ANNUAL: 'Annuel', SEMESTRIAL: 'Semestriel', TRIMESTRIAL: 'Trimestriel', MONTHLY: 'Mensuel', CUSTOM: 'Personnalisé',
};

const scheduleTypeColors: Record<string, string> = {
  ANNUAL: 'bg-blue-100 text-blue-700', SEMESTRIAL: 'bg-green-100 text-green-700', TRIMESTRIAL: 'bg-purple-100 text-purple-700', MONTHLY: 'bg-amber-100 text-amber-700', CUSTOM: 'bg-gray-100 text-gray-700',
};

// Fee type labels and colors removed - now handled in AncillaryFeesPage
// Discount type icons/labels/colors removed - now handled in DiscountsPage

const triggerLabels: Record<string, string> = {
  BEFORE_DUE: 'Avant échéance', AFTER_DUE: 'Après échéance', OVERDUE_15: '15 jours retard', OVERDUE_30: '30 jours retard', OVERDUE_60: '60 jours retard', SUSPENSION_WARNING: 'Avert. suspension',
};

const triggerColors: Record<string, string> = {
  BEFORE_DUE: 'bg-blue-100 text-blue-700', AFTER_DUE: 'bg-amber-100 text-amber-700', OVERDUE_15: 'bg-orange-100 text-orange-700', OVERDUE_30: 'bg-red-100 text-red-700', OVERDUE_60: 'bg-red-100 text-red-700', SUSPENSION_WARNING: 'bg-red-100 text-red-700',
};

const channelIcons: Record<string, React.ReactNode> = {
  EMAIL: <Mail size={16} />, SMS: <MessageSquare size={16} />, IN_APP: <Bell size={16} />,
};

export const TuitionFeesPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<ActiveSection>('dashboard');
  // User-visible error for actions whose failures were previously silent
  const [actionError, setActionError] = useState<string | null>(null);
  // Pending settings change that would reset all tariffs (red-flag confirmation)
  const [settingsResetConfirm, setSettingsResetConfirm] = useState<{
    differsByNiveau: boolean;
    differsByCampus: boolean;
    differsByTypeFormation: boolean;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Permissions - Granular checks for each section
  const { hasPermission, hasAnyPermission } = usePermissions();

  // Grille Tarifaire
  const canViewTuition = hasPermission(Permission.FINANCE_TUITION_VIEW);
  const canManageTuition = hasPermission(Permission.FINANCE_TUITION_MANAGE);

  // Frais Annexes (separate from Grille Tarifaire)
  const canViewAncillary = hasPermission(Permission.FINANCE_ANCILLARY_VIEW);
  const canManageAncillary = hasPermission(Permission.FINANCE_ANCILLARY_MANAGE);

  // Échéanciers Types
  const canViewSchedules = hasPermission(Permission.FINANCE_SCHEDULE_VIEW);
  const canManageSchedules = hasPermission(Permission.FINANCE_SCHEDULE_MANAGE);

  // Remises & Bourses
  const canViewDiscounts = hasPermission(Permission.FINANCE_DISCOUNT_VIEW);
  const canManageDiscounts = hasPermission(Permission.FINANCE_DISCOUNT_MANAGE);

  // Paramètres Relances
  const canViewReminders = hasPermission(Permission.FINANCE_REMINDER_VIEW);
  const canManageReminders = hasPermission(Permission.FINANCE_REMINDER_MANAGE);

  // Modal states for grille section
  const [formModalGrid, setFormModalGrid] = useState<TuitionGridWithRelations | null | undefined>(undefined);
  const [archiveModalGrid, setArchiveModalGrid] = useState<TuitionGridWithRelations | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  // Configure modal state - stores filière info and existing grids when configuring from table row
  const [configureModalFiliere, setConfigureModalFiliere] = useState<{ id: string; name: string; existingGrids: TuitionGridWithRelations[] } | null>(null);

  // Grouped display state
  const [groupedFilieres, setGroupedFilieres] = useState<FiliereWithOptions[]>([]);
  const [isLoadingFilieres, setIsLoadingFilieres] = useState(false);
  const [selectedCampusId, setSelectedCampusId] = useState<string | undefined>(undefined);

  // Queries for grille section - no longer needs academic year
  const grilleQuery: TuitionGridQuery = {
    includeArchived: showArchived,
    search: searchTerm || undefined,
  };

  const { refetch: refetchGrids } = useTuitionGrids(grilleQuery);
  const { data: dashboardData, isLoading: isLoadingDashboard } = useDashboardSummary();

  // Fetch ancillary fees for dashboard stats (include archived for accurate total count)
  const { data: ancillaryFeesData, isLoading: isLoadingAncillaryFees } = useAncillaryFees({ includeArchived: true });

  // Fetch discounts for dashboard stats (include archived for accurate total count)
  const { data: discountsData, isLoading: isLoadingDiscounts, error: discountsError } = useDiscounts({ includeArchived: true });

  // Fetch payment schedules for dashboard stats and echeanciers section
  const [includeArchivedSchedules, setIncludeArchivedSchedules] = useState(false);
  const { data: paymentSchedulesData, isLoading: isLoadingSchedules, refetch: refetchSchedules } = usePaymentSchedules({ includeArchived: includeArchivedSchedules });
  const setDefaultScheduleMutation = useSetDefaultPaymentSchedule();
  const duplicateScheduleMutation = useDuplicatePaymentSchedule();
  const archiveScheduleMutation = useArchivePaymentSchedule();
  const restoreScheduleMutation = useRestorePaymentSchedule();
  const deleteScheduleMutation = useDeletePaymentSchedule();

  // Fetch reminder configurations
  const { data: remindersData, isLoading: isLoadingReminders } = useReminderConfigs();
  const reminders = remindersData?.data || [];
  const updateReminderMutation = useUpdateReminder();
  const deleteReminderMutation = useDeleteReminder();

  // Reminder form modal state
  const [showReminderFormModal, setShowReminderFormModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<ReminderConfig | null>(null);

  // Stats time filter state
  const [statsTimeFilter, setStatsTimeFilter] = useState<'week' | 'month' | 'quarter' | 'year'>('month');

  // Payment schedule form modal state
  const [scheduleFormModal, setScheduleFormModal] = useState<PaymentScheduleTemplate | null | undefined>(undefined);
  // Payment schedule assign modal state
  const [scheduleAssignModal, setScheduleAssignModal] = useState<PaymentScheduleTemplate | null>(null);
  // Confirm dialog state for schedules
  const [scheduleConfirmDialog, setScheduleConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'archive' | 'restore' | 'danger';
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    if (discountsError) {
      console.error('Discounts query error:', discountsError);
    }
  }, [discountsError]);

  // Settings and campus queries
  const { data: settings, isLoading: isLoadingSettings } = useTuitionGridSettings();
  const { data: campusInfo } = useCampusInfo();
  const saveSettingsMutation = useSaveTuitionGridSettings();

  // Grouped grids query (for new display mode)
  const { data: groupedGrids, isLoading: isLoadingGroupedGrids, refetch: refetchGroupedGrids } = useTuitionGridsGrouped(
    selectedCampusId,
    showArchived
  );

  // Load grouped filières on mount and when showArchived changes
  useEffect(() => {
    const loadGroupedFilieres = async () => {
      setIsLoadingFilieres(true);
      try {
        // When showArchived is true, we want to include inactive filières (activeOnly = false)
        const filieres = await fetchFilieresGrouped(!showArchived);
        setGroupedFilieres(filieres);
      } catch (err) {
        console.error('Error loading grouped filières:', err);
      } finally {
        setIsLoadingFilieres(false);
      }
    };
    loadGroupedFilieres();
  }, [showArchived]);

  // Sync mutation
  const syncMutation = useSyncFilieres();

  // Calculate stats for dashboard cards
  const totalTuitionConfigs = dashboardData?.totalConfigurations || 0;
  const avgTuition = dashboardData?.averageTuition || 0;
  // Use real payment schedules data from API
  const paymentSchedules = paymentSchedulesData?.data || [];
  const activeSchedules = paymentSchedules.filter((t: PaymentScheduleTemplate) => t.isActive).length;
  const totalScheduleUsage = paymentSchedules.reduce((sum: number, t: PaymentScheduleTemplate) => sum + (t._count?.studentSchedules || 0), 0);
  // Use real ancillary fees data from API
  const ancillaryFees = ancillaryFeesData?.data || [];
  const activeFees = ancillaryFees.filter(f => f.isActive).length;
  const totalFees = ancillaryFees.length;
  // Use real discount data from API
  const discounts = discountsData?.data || [];
  const activeDiscounts = discounts.filter(d => d.isActive).length;
  const totalDiscountBeneficiaries = discounts.reduce((sum, d) => sum + (d._count?.studentDiscounts || 0), 0);
  const activeReminders = reminders.filter(r => r.isActive).length;

  const toggleReminderStatus = async (id: string) => {
    const reminder = reminders.find(r => r.id === id);
    if (!reminder) return;

    try {
      await updateReminderMutation.mutateAsync({
        id,
        data: { isActive: !reminder.isActive }
      });
    } catch (error) {
      console.error('Error toggling reminder status:', error);
      setActionError(error instanceof Error ? error.message : 'Erreur lors de la modification de la relance');
    }
  };

  const handleDeleteReminder = async (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette relance ?')) return;

    try {
      await deleteReminderMutation.mutateAsync(id);
    } catch (error) {
      console.error('Error deleting reminder:', error);
      setActionError(error instanceof Error ? error.message : 'Erreur lors de la suppression de la relance');
    }
  };

  const formatDaysOffset = (days: number) => {
    if (days === 0) return 'Jour J';
    if (days < 0) return `J${days}`;
    return `J+${days}`;
  };

  // Calculate date range based on stats time filter - memoized to prevent infinite re-renders
  const { start, end } = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date();

    switch (statsTimeFilter) {
      case 'week':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(startDate.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(startDate.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
    }

    return { start: startDate.toISOString(), end: endDate.toISOString() };
  }, [statsTimeFilter]);

  // Fetch reminder history with filter
  const REMINDERS_PER_PAGE = 50;
  const [reminderPage, setReminderPage] = useState(1);
  // Reset to the first page whenever the period filter changes.
  useEffect(() => { setReminderPage(1); }, [statsTimeFilter]);

  const { data: historyData, isLoading: isLoadingHistory } = useReminderHistory({
    startDate: start,
    endDate: end,
    status: 'REMINDER_SENT',
    page: reminderPage,
    limit: REMINDERS_PER_PAGE,
  });

  const reminderHistory = historyData?.data || [];

  // Total sent in the period comes from meta.total (the list itself is paginated)
  const notificationsSent = historyData?.meta?.total ?? reminderHistory.length;


  const handleSync = async () => {
    try {
      await syncMutation.mutateAsync();
      refetchGrids();
    } catch (err) {
      console.error('Sync error:', err);
      setActionError(err instanceof Error ? err.message : 'Erreur lors de la synchronisation des filières');
    }
  };

  const handleModalSuccess = () => {
    refetchGrids();
    refetchGroupedGrids();
  };

  // Dashboard Cards
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Frais & Relances</h1>
        <p className="text-gray-500 mt-1">Configuration complète des tarifs, échéanciers, frais annexes, remises et relances automatiques</p>
      </div>

      {/* Summary Cards Grid - Only show cards the user has VIEW permission for */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {/* Grille Tarifaire Card - requires FINANCE_TUITION_VIEW */}
        {canViewTuition && (
          <button
            onClick={() => setActiveSection('grille')}
            className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-primary-300 transition-all text-left group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
                <FileSpreadsheet className="h-6 w-6 text-white" />
              </div>
              <span className="text-xs font-medium text-gray-400 group-hover:text-primary-600 transition-colors">
                Voir tout →
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 text-lg mb-1">Grille Tarifaire</h3>
            <p className="text-sm text-gray-500 mb-4">Tarifs par filière et niveau</p>
            <div className="flex items-baseline gap-2">
              {isLoadingDashboard ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : (
                <>
                  <span className="text-3xl font-bold text-gray-900">{totalTuitionConfigs}</span>
                  <span className="text-sm text-gray-500">configurations</span>
                </>
              )}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Moyenne: <span className="font-medium text-gray-700">{formatCurrency(avgTuition)}</span>
            </div>
          </button>
        )}

        {/* Frais Annexes Card - requires FINANCE_ANCILLARY_VIEW */}
        {canViewAncillary && (
          <button
            onClick={() => setActiveSection('annexes')}
            className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-primary-300 transition-all text-left group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25">
                <Receipt className="h-6 w-6 text-white" />
              </div>
              <span className="text-xs font-medium text-gray-400 group-hover:text-primary-600 transition-colors">
                Voir tout →
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 text-lg mb-1">Frais Annexes</h3>
            <p className="text-sm text-gray-500 mb-4">Attestations, duplicatas, etc.</p>
            <div className="flex items-baseline gap-2">
              {isLoadingAncillaryFees ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : (
                <>
                  <span className="text-3xl font-bold text-gray-900">{activeFees}</span>
                  <span className="text-sm text-gray-500">/ {totalFees} types</span>
                </>
              )}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              actifs sur le catalogue
            </div>
          </button>
        )}

        {/* Remises & Bourses Card - requires FINANCE_DISCOUNT_VIEW */}
        {canViewDiscounts && (
          <button
            onClick={() => setActiveSection('remises')}
            className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-primary-300 transition-all text-left group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
                <Award className="h-6 w-6 text-white" />
              </div>
              <span className="text-xs font-medium text-gray-400 group-hover:text-primary-600 transition-colors">
                Voir tout →
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 text-lg mb-1">Remises & Bourses</h3>
            <p className="text-sm text-gray-500 mb-4">Réductions applicables</p>
            <div className="flex items-baseline gap-2">
              {isLoadingDiscounts ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : (
                <>
                  <span className="text-3xl font-bold text-gray-900">{activeDiscounts}</span>
                  <span className="text-sm text-gray-500">/ {discounts.length} types</span>
                </>
              )}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              <span className="font-medium text-gray-700">{totalDiscountBeneficiaries}</span> bénéficiaire{totalDiscountBeneficiaries > 1 ? 's' : ''}
            </div>
          </button>
        )}

        {/* Échéanciers Card - requires FINANCE_SCHEDULE_VIEW */}
        {canViewSchedules && (
          <button
            onClick={() => setActiveSection('echeanciers')}
            className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-primary-300 transition-all text-left group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg shadow-green-500/25">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <span className="text-xs font-medium text-gray-400 group-hover:text-primary-600 transition-colors">
                Voir tout →
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 text-lg mb-1">Échéanciers Types</h3>
            <p className="text-sm text-gray-500 mb-4">Plans de paiement disponibles</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-gray-900">{activeSchedules}</span>
              <span className="text-sm text-gray-500">actifs</span>
            </div>
            <div className="mt-2 text-sm text-gray-500">
              <span className="font-medium text-gray-700">{totalScheduleUsage}</span> utilisations
            </div>
          </button>
        )}

        {/* Paramètres Relances Card - requires FINANCE_REMINDER_VIEW */}
        {canViewReminders && (
          <button
            onClick={() => setActiveSection('relances')}
            className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-primary-300 transition-all text-left group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg shadow-red-500/25">
                <Bell className="h-6 w-6 text-white" />
              </div>
              <span className="text-xs font-medium text-gray-400 group-hover:text-primary-600 transition-colors">
                Voir tout →
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 text-lg mb-1">Paramètres Relances</h3>
            <p className="text-sm text-gray-500 mb-4">Relances automatiques</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-gray-900">{activeReminders}</span>
              <span className="text-sm text-gray-500">/ {reminders.length} règles</span>
            </div>
            <div className="mt-2 text-sm text-gray-500">
              actives sur le système
            </div>
          </button>
        )}
      </div>

      {/* Quick Overview Section - conditionally show based on permissions */}
      {(canViewTuition || canViewAncillary || canViewDiscounts) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Tuition Grids Preview - requires FINANCE_TUITION_VIEW */}
          {canViewTuition && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Aperçu Grille Tarifaire</h3>
                <button
                  onClick={() => setActiveSection('grille')}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Voir tout
                </button>
              </div>
              <div className="space-y-3">
                {isLoadingDashboard || isLoadingSettings ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                  </div>
                ) : dashboardData?.items && dashboardData.items.length > 0 ? (
                  // Group by filière and show max TOTAL (not just scolarité)
                  (() => {
                    // Group items by filière for display
                    const groupedByFiliere = dashboardData.items.reduce((acc, item) => {
                      if (!acc[item.filiereId]) {
                        acc[item.filiereId] = {
                          filiereName: item.filiereName,
                          filiereCode: item.filiereCode,
                          items: [],
                        };
                      }
                      acc[item.filiereId].items.push(item);
                      return acc;
                    }, {} as Record<string, { filiereName: string; filiereCode: string; items: typeof dashboardData.items }>);

                    const filiereGroups = Object.values(groupedByFiliere).slice(0, 3);

                    return filiereGroups.map((group, index) => {
                      // Get max TOTAL (tuition + registration)
                      const maxTotal = Math.max(...group.items.map(i => i.total));
                      // Check if there are multiple configurations
                      const hasMultipleConfigs = group.items.length > 1;

                      return (
                        <div key={`${group.filiereCode}-${index}`} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <p className="font-medium text-gray-900 text-sm">{group.filiereName}</p>
                          <span className="font-semibold text-primary-600">
                            {hasMultipleConfigs ? 'Jusqu\'à ' : ''}{formatCurrency(maxTotal)}
                          </span>
                        </div>
                      );
                    });
                  })()
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Aucune grille tarifaire configurée
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Active Discounts Preview - requires FINANCE_DISCOUNT_VIEW */}
          {canViewDiscounts && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Remises Populaires</h3>
                <button
                  onClick={() => setActiveSection('remises')}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Voir tout
                </button>
              </div>
              <div className="space-y-3">
                {isLoadingDiscounts ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                  </div>
                ) : discounts.filter(d => d.isActive).length > 0 ? (
                  discounts.filter(d => d.isActive).slice(0, 3).map((discount) => (
                    <div key={discount.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-purple-100">
                          <Award className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{discount.name}</p>
                          <p className="text-xs text-gray-500">{discount._count?.studentDiscounts || 0} bénéficiaire(s)</p>
                        </div>
                      </div>
                      <span className="font-semibold text-purple-600">
                        {discount.percentageValue !== null ? `${discount.percentageValue}%` : formatCurrency(discount.fixedAmount || 0)}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    Aucune remise configurée
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Handle save settings. Changing a pricing dimension resets EVERY tariff to
  // 0 MAD — the backend refuses with CONFIRM_RESET_REQUIRED unless confirmed,
  // and we surface that as a red-flag dialog before retrying with the flag.
  const handleSaveSettings = async (newSettings: Omit<TuitionGridSettings, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>) => {
    const payload = {
      differsByNiveau: newSettings.differsByNiveau,
      differsByCampus: newSettings.differsByCampus,
      differsByTypeFormation: newSettings.differsByTypeFormation,
    };
    try {
      await saveSettingsMutation.mutateAsync(payload);
      refetchGroupedGrids();
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      if (message.includes('CONFIRM_RESET_REQUIRED')) {
        setSettingsResetConfirm(payload);
        return;
      }
      throw err;
    }
  };

  // Handle edit from grouped table - find the filière and open config modal with existing grids
  // If niveau or typeFormation is specified, filter to only that specific grid(s)
  const handleEditFromGrouped = (filiereId: string, niveau?: number, typeFormation?: TypeFormation) => {
    const filiere = groupedFilieres.find(f => f.id === filiereId);
    if (filiere) {
      // Get existing grids for this filière
      let filiereGrids = (groupedGrids || []).filter(g => g.filiereId === filiereId);

      // If a specific type formation is specified (clicking on Formation Initiale/Continue row)
      // Pass only that specific grid to edit just that one
      if (typeFormation !== undefined) {
        const specificGrid = filiereGrids.find(g =>
          g.typeFormation === typeFormation &&
          (niveau === undefined || g.niveau === niveau)
        );
        if (specificGrid) {
          // Open single-grid edit mode with this specific grid
          setFormModalGrid(specificGrid);
          return;
        }
      }

      // If niveau is specified but not type, filter by niveau
      if (niveau !== undefined && typeFormation === undefined) {
        filiereGrids = filiereGrids.filter(g => g.niveau === niveau);
      }

      // Open bulk edit mode with all/filtered grids
      setConfigureModalFiliere({ id: filiereId, name: filiere.filiere, existingGrids: filiereGrids });
    }
  };

  // Handle configure filière from grouped table (new configuration)
  const handleConfigureFiliere = (filiereId: string) => {
    const filiere = groupedFilieres.find(f => f.id === filiereId);
    if (filiere) {
      // No existing grids for new configuration
      setConfigureModalFiliere({ id: filiereId, name: filiere.filiere, existingGrids: [] });
    }
  };

  // Grille Tarifaire Section - Refactored with grouped display
  const renderGrille = () => {
    const hasMultipleCampuses = campusInfo?.hasMultipleCampuses || false;

    return (
      <div className="space-y-6">
        {/* Header with back button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => { setActiveSection('dashboard'); setSearchTerm(''); }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Grille Tarifaire</h1>
              <p className="text-gray-500 mt-1">
                Configuration des frais de scolarité par filière et niveau
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Sync button */}
            <button
              onClick={handleSync}
              disabled={syncMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {syncMutation.isPending ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <RefreshCw size={18} />
              )}
              Actualiser
            </button>
            {/* Settings button - only for users with manage permission */}
            {canManageTuition && (
              <button
                onClick={() => setShowSettingsModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Settings size={18} />
                Paramètres
              </button>
            )}
          </div>
        </div>

        {/* Search and filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par filière, niveau..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Campus selector - only shown when multiple campuses exist and differsByCampus is enabled */}
            {hasMultipleCampuses && settings?.differsByCampus && (
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                <select
                  value={selectedCampusId || ''}
                  onChange={(e) => setSelectedCampusId(e.target.value || undefined)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                >
                  <option value="">Tous les campus</option>
                  {campusInfo?.campuses.map((campus) => (
                    <option key={campus.id} value={campus.id}>
                      {campus.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Show archived toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-600">Afficher les archivées</span>
            </label>
          </div>
        </div>

        {/* Grouped Table (new display) */}
        <TuitionGridGroupedTable
          filieres={groupedFilieres}
          grids={groupedGrids || []}
          settings={settings || null}
          campusInfo={campusInfo || null}
          selectedCampusId={selectedCampusId}
          isLoading={isLoadingFilieres || isLoadingGroupedGrids || isLoadingSettings}
          canManage={canManageTuition}
          onEdit={handleEditFromGrouped}
          onConfigureFiliere={handleConfigureFiliere}
        />

        {formModalGrid !== undefined && (
          <TuitionGridFormModal
            grid={formModalGrid}
            preselectedCampusId={selectedCampusId}
            settings={settings}
            onClose={() => setFormModalGrid(undefined)}
            onSuccess={handleModalSuccess}
          />
        )}

        {/* Configure Filière Modal - opens when clicking Configurer on a row */}
        {configureModalFiliere && (
          <TuitionGridFormModal
            preselectedFiliereId={configureModalFiliere.id}
            preselectedFiliereName={configureModalFiliere.name}
            preselectedCampusId={selectedCampusId}
            existingGrids={configureModalFiliere.existingGrids}
            settings={settings}
            onClose={() => setConfigureModalFiliere(null)}
            onSuccess={handleModalSuccess}
          />
        )}

        {archiveModalGrid && (
          <TuitionGridArchiveModal
            grid={archiveModalGrid}
            onClose={() => setArchiveModalGrid(null)}
            onSuccess={handleModalSuccess}
          />
        )}

        {/* Settings Modal */}
        {showSettingsModal && (
          <TuitionGridSettingsModal
            settings={settings || null}
            campusInfo={campusInfo || null}
            isLoading={isLoadingSettings}
            hasExistingGrids={(groupedGrids || []).some(g => g.tuitionAmount > 0)}
            onSave={handleSaveSettings}
            onClose={() => setShowSettingsModal(false)}
          />
        )}
      </div>
    );
  };

  // Échéanciers Section - Now using real API data
  const renderEcheanciers = () => {
    const filteredTemplates = paymentSchedules.filter((template: PaymentScheduleTemplate) =>
      template.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleDuplicate = async (template: PaymentScheduleTemplate) => {
      try {
        await duplicateScheduleMutation.mutateAsync({
          id: template.id,
          data: { newName: `${template.name} (copie)` },
        });
        refetchSchedules();
      } catch (err) {
        console.error('Error duplicating schedule:', err);
        setActionError(err instanceof Error ? err.message : "Erreur lors de la duplication de l'échéancier");
      }
    };

    const handleSetDefault = async (template: PaymentScheduleTemplate) => {
      try {
        await setDefaultScheduleMutation.mutateAsync(template.id);
        refetchSchedules();
      } catch (err) {
        console.error('Error setting default schedule:', err);
        setActionError(err instanceof Error ? err.message : "Erreur lors de la définition de l'échéancier par défaut");
      }
    };

    const handleArchiveClick = (template: PaymentScheduleTemplate) => {
      setScheduleConfirmDialog({
        isOpen: true,
        title: "Archiver l'écheancier",
        message: `Voulez-vous vraiment archiver "${template.name}" ? Cette action peut etre annulee.`,
        variant: 'archive',
        onConfirm: async () => {
          try {
            await archiveScheduleMutation.mutateAsync(template.id);
            refetchSchedules();
            setScheduleConfirmDialog(null);
          } catch (err) {
            console.error('Error archiving schedule:', err);
            setScheduleConfirmDialog(null);
            setActionError(err instanceof Error ? err.message : "Erreur lors de l'archivage de l'échéancier");
          }
        },
      });
    };

    const handleRestoreClick = (template: PaymentScheduleTemplate) => {
      setScheduleConfirmDialog({
        isOpen: true,
        title: "Restaurer l'echeancier",
        message: `Voulez-vous restaurer "${template.name}" ? Il sera de nouveau disponible.`,
        variant: 'restore',
        onConfirm: async () => {
          try {
            await restoreScheduleMutation.mutateAsync(template.id);
            refetchSchedules();
            setScheduleConfirmDialog(null);
          } catch (err) {
            console.error('Error restoring schedule:', err);
            setScheduleConfirmDialog(null);
            setActionError(err instanceof Error ? err.message : "Erreur lors de la restauration de l'échéancier");
          }
        },
      });
    };

    const handleDeleteClick = (template: PaymentScheduleTemplate) => {
      setScheduleConfirmDialog({
        isOpen: true,
        title: "Supprimer l'echeancier",
        message: `Voulez-vous vraiment supprimer "${template.name}" ? Cette action est irreversible.`,
        variant: 'danger',
        onConfirm: async () => {
          try {
            await deleteScheduleMutation.mutateAsync(template.id);
            refetchSchedules();
            setScheduleConfirmDialog(null);
          } catch (err) {
            setScheduleConfirmDialog(null);
            setActionError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
          }
        },
      });
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => { setActiveSection('dashboard'); setSearchTerm(''); }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Echeanciers Types</h1>
              <p className="text-gray-500 mt-1">Modeles d'echeanciers de paiement assignables aux etudiants</p>
            </div>
          </div>
          {canManageSchedules && (
            <button
              onClick={() => setScheduleFormModal(null)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus size={20} />
              Nouvel Echeancier
            </button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher un echeancier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={includeArchivedSchedules}
                onChange={(e) => setIncludeArchivedSchedules(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              Inclure archives
            </label>
            <button
              onClick={() => refetchSchedules()}
              className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Rafraichir"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>

        {isLoadingSchedules ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template: PaymentScheduleTemplate) => (
              <div
                key={template.id}
                className={`bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow ${!template.isActive ? 'opacity-70 bg-gray-50' : ''}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`h-12 w-12 rounded-xl ${template.isActive ? 'bg-primary-100' : 'bg-gray-200'} flex items-center justify-center`}>
                    <Calendar className={`h-6 w-6 ${template.isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    {template.isDefault && (
                      <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">
                        Par defaut
                      </span>
                    )}
                    <span className={`px-2 py-1 text-xs rounded-full ${scheduleTypeColors[template.type]}`}>
                      {scheduleTypeLabels[template.type]}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className={`font-semibold ${template.isActive ? 'text-gray-900' : 'text-gray-500'}`}>{template.name}</h3>
                  {!template.isActive && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                      Archive
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <span>{template.installments.length} echeance(s)</span>
                  <span>-</span>
                  <span>{template._count?.studentSchedules || 0} utilisations</span>
                </div>
                {template.gracePeriodDays > 0 && (
                  <p className="text-xs text-gray-400 mb-2">
                    Delai de grace: {template.gracePeriodDays} jour(s)
                  </p>
                )}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className={`px-2 py-1 text-xs rounded-full ${template.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {template.isActive ? 'Actif' : 'Archive'}
                  </span>
                  <div className="flex items-center gap-1">
                    {template.isActive ? (
                      <>
                        {canManageSchedules && !template.isDefault && (
                          <button
                            onClick={() => handleSetDefault(template)}
                            className="p-1.5 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                            title="Definir par defaut"
                          >
                            <Star size={18} />
                          </button>
                        )}
                        {canManageSchedules && (
                          <button
                            onClick={() => setScheduleAssignModal(template)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Assigner aux etudiants"
                          >
                            <Users size={18} />
                          </button>
                        )}
                        {canManageSchedules && (
                          <button
                            onClick={() => handleDuplicate(template)}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="Dupliquer"
                          >
                            <Copy size={18} />
                          </button>
                        )}
                        {canManageSchedules && (
                          <button
                            onClick={() => setScheduleFormModal(template)}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                            title="Modifier"
                          >
                            <Edit2 size={18} />
                          </button>
                        )}
                        {canManageSchedules && (
                          <button
                            onClick={() => handleArchiveClick(template)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              template.isDefault
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                            }`}
                            title={template.isDefault ? "Impossible d'archiver l'échéancier par défaut" : "Archiver"}
                            disabled={template.isDefault || archiveScheduleMutation.isPending}
                          >
                            <Archive size={18} />
                          </button>
                        )}
                        {canManageSchedules && (
                          <button
                            onClick={() => handleDeleteClick(template)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              template.isDefault || (template._count?.studentSchedules || 0) > 0
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                            }`}
                            title={
                              template.isDefault
                                ? "Impossible de supprimer l'échéancier par défaut"
                                : (template._count?.studentSchedules || 0) > 0
                                  ? "Impossible de supprimer (en cours d'utilisation)"
                                  : "Supprimer"
                            }
                            disabled={template.isDefault || deleteScheduleMutation.isPending || (template._count?.studentSchedules || 0) > 0}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        {canManageSchedules && (
                          <button
                            onClick={() => handleRestoreClick(template)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Restaurer"
                            disabled={restoreScheduleMutation.isPending}
                          >
                            <RotateCcw size={18} />
                          </button>
                        )}
                        {canManageSchedules && (
                          <button
                            onClick={() => handleDeleteClick(template)}
                            className={`p-1.5 rounded-lg transition-colors ${
                              (template._count?.studentSchedules || 0) > 0
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                            }`}
                            title={(template._count?.studentSchedules || 0) > 0 ? "Impossible de supprimer (en cours d'utilisation)" : "Supprimer"}
                            disabled={deleteScheduleMutation.isPending || (template._count?.studentSchedules || 0) > 0}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!isLoadingSchedules && filteredTemplates.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun echeancier trouve</h3>
            <p className="mt-1 text-sm text-gray-500">
              {canManageSchedules ? "Creez un nouvel echeancier type pour commencer." : "Aucun echeancier disponible pour le moment."}
            </p>
            {canManageSchedules && (
              <button
                onClick={() => setScheduleFormModal(null)}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus size={18} />
                Creer un echeancier
              </button>
            )}
          </div>
        )}

        {/* Pagination info */}
        {paymentSchedulesData?.meta && paymentSchedulesData.meta.total > 0 && (
          <div className="text-sm text-gray-500 text-center">
            {paymentSchedulesData.meta.total} echeancier{paymentSchedulesData.meta.total > 1 ? 's' : ''} au total
          </div>
        )}
      </div>
    );
  };

  // Frais Annexes Section - Now uses the dedicated AncillaryFeesPage component
  const renderAnnexes = () => (
    <div className="space-y-6">
      {/* Back button to return to dashboard */}
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => { setActiveSection('dashboard'); setSearchTerm(''); }}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft size={20} className="text-gray-600" />
        </button>
        <span className="text-sm text-gray-500">Retour au tableau de bord</span>
      </div>
      {/* Render the dedicated AncillaryFeesPage component */}
      <AncillaryFeesPage />
    </div>
  );

  // Remises & Bourses Section - Now uses the dedicated DiscountsPage component
  const renderRemises = () => (
    <div className="space-y-6">
      {/* Back button to return to dashboard */}
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => { setActiveSection('dashboard'); setSearchTerm(''); }}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft size={20} className="text-gray-600" />
        </button>
        <span className="text-sm text-gray-500">Retour au tableau de bord</span>
      </div>
      {/* Render the dedicated DiscountsPage component */}
      <DiscountsPage embedded />
    </div>
  );

  // Paramètres Relances Section
  const renderRelances = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => { setActiveSection('dashboard'); setSearchTerm(''); }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Paramètres Relances</h1>
            <p className="text-gray-500 mt-1">Configuration des relances automatiques de paiement</p>
          </div>
        </div>
        {canManageReminders && (
          <button
            onClick={() => {
              setSelectedReminder(null);
              setShowReminderFormModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={20} />
            Nouvelle Relance
          </button>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex gap-3">
          <Bell className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">Relances Automatiques</h3>
            <p className="text-sm text-blue-700 mt-1">
              Les relances sont envoyées automatiquement selon le calendrier configuré.
              Les étudiants reçoivent les notifications sur les canaux sélectionnés.
            </p>
          </div>
        </div>
      </div>

      {/* Timeline View */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-900 mb-6">Chronologie des Relances</h2>

        {isLoadingReminders ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : reminders.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Aucune relance configurée</p>
            {canManageReminders && (
              <button
                onClick={() => {
                  setSelectedReminder(null);
                  setShowReminderFormModal(true);
                }}
                className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
              >
                Créer votre première relance
              </button>
            )}
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>

            <div className="space-y-6">
              {reminders
                .sort((a, b) => a.daysOffset - b.daysOffset)
                .map((reminder) => (
                <div key={reminder.id} className="relative flex gap-4">
                  {/* Timeline dot */}
                  <div
                    className={`relative z-10 h-16 w-16 rounded-full flex items-center justify-center text-sm font-bold ${
                      reminder.isActive
                        ? 'bg-primary-100 text-primary-700 border-2 border-primary-300'
                        : 'bg-gray-100 text-gray-400 border-2 border-gray-200'
                    }`}
                  >
                    {formatDaysOffset(reminder.daysOffset)}
                  </div>

                  {/* Card */}
                  <div
                    className={`flex-1 p-4 rounded-xl border ${
                      reminder.isActive
                        ? 'bg-white border-gray-200'
                        : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3
                            className={`font-medium ${
                              reminder.isActive ? 'text-gray-900' : 'text-gray-400'
                            }`}
                          >
                            {reminder.name || 'Sans nom'}
                          </h3>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${
                              triggerColors[reminder.trigger]
                            }`}
                          >
                            {triggerLabels[reminder.trigger]}
                          </span>
                        </div>

                        <p
                          className={`text-sm mb-3 ${
                            reminder.isActive ? 'text-gray-600' : 'text-gray-400'
                          }`}
                        >
                          {reminder.messageTemplate || ''}
                        </p>

                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                              reminder.isActive
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-50 text-gray-400'
                            }`}
                          >
                            <Bell size={14} />
                            Notification in-app
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {canManageReminders && (
                          <>
                            <button
                              onClick={() => toggleReminderStatus(reminder.id)}
                              className={reminder.isActive ? 'text-green-600' : 'text-gray-400'}
                              title={reminder.isActive ? 'Désactiver' : 'Activer'}
                            >
                              {reminder.isActive ? (
                                <ToggleRight size={28} />
                              ) : (
                                <ToggleLeft size={28} />
                              )}
                            </button>
                            <button
                              onClick={() => {
                                setSelectedReminder(reminder);
                                setShowReminderFormModal(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                              title="Modifier"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteReminder(reminder.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Statistics Section - Notifications Only */}
      <div className="space-y-6">
        {/* Time Filter Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Statistiques des relances</h2>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Période:</label>
            <select
              value={statsTimeFilter}
              onChange={(e) => setStatsTimeFilter(e.target.value as any)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="week">7 derniers jours</option>
              <option value="month">30 derniers jours</option>
              <option value="quarter">3 derniers mois</option>
              <option value="year">12 derniers mois</option>
            </select>
          </div>
        </div>

        {/* Summary Card - Notifications */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Bell className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-blue-100 text-sm">Notifications envoyées</p>
              <p className="text-3xl font-bold">{isLoadingHistory ? '...' : notificationsSent}</p>
            </div>
          </div>
          <p className="text-blue-100 text-sm mt-2">
            {statsTimeFilter === 'week' && '7 derniers jours'}
            {statsTimeFilter === 'month' && '30 derniers jours'}
            {statsTimeFilter === 'quarter' && '3 derniers mois'}
            {statsTimeFilter === 'year' && '12 derniers mois'}
          </p>
        </div>

        {/* Recipients List */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-600" />
            Destinataires ({notificationsSent})
          </h3>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {isLoadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : reminderHistory.length > 0 ? (
              reminderHistory.map((reminder) => {
                // The history endpoint nests the student under account.student
                // (and it may be null); the manual-send response puts it at the
                // top level. Tolerate both, and never crash on a missing student.
                const student =
                  (reminder as any).student ?? (reminder as any).account?.student ?? null;
                return (
                <div
                  key={reminder.id}
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {student ? `${student.firstName} ${student.lastName}` : 'Étudiant'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(reminder.sentAt!).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-medium text-red-600">
                      {formatCurrency(reminder.account?.balance ?? 0)}
                    </p>
                  </div>
                </div>
                );
              })
            ) : (
              <p className="text-center py-12 text-gray-500 text-sm">
                Aucune notification envoyée dans cette période
              </p>
            )}
          </div>

          {/* Pagination — navigate the full list of recipients */}
          {(historyData?.meta?.totalPages ?? 1) > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
              <p className="text-xs text-gray-500">
                {notificationsSent} destinataire{notificationsSent > 1 ? 's' : ''} au total
                {' · '}
                {(reminderPage - 1) * REMINDERS_PER_PAGE + 1}–
                {Math.min(reminderPage * REMINDERS_PER_PAGE, notificationsSent)}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setReminderPage((p) => Math.max(1, p - 1))}
                  disabled={reminderPage <= 1 || isLoadingHistory}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <span className="text-xs text-gray-600">
                  Page {reminderPage} / {historyData?.meta?.totalPages ?? 1}
                </span>
                <button
                  onClick={() =>
                    setReminderPage((p) => Math.min(historyData?.meta?.totalPages ?? 1, p + 1))
                  }
                  disabled={reminderPage >= (historyData?.meta?.totalPages ?? 1) || isLoadingHistory}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render appropriate section
  return (
    <div className="space-y-6">
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Erreur</p>
            <p className="text-sm text-red-700">{actionError}</p>
          </div>
          <button onClick={() => setActionError(null)} className="ml-auto text-red-600 text-sm font-medium">
            Fermer
          </button>
        </div>
      )}
      {activeSection === 'dashboard' && renderDashboard()}
      {activeSection === 'grille' && renderGrille()}
      {activeSection === 'echeanciers' && renderEcheanciers()}
      {activeSection === 'annexes' && renderAnnexes()}
      {activeSection === 'remises' && renderRemises()}
      {activeSection === 'relances' && renderRelances()}

      {/* Payment Schedule Form Modal */}
      {scheduleFormModal !== undefined && (
        <PaymentScheduleFormModal
          schedule={scheduleFormModal}
          onClose={() => setScheduleFormModal(undefined)}
          onSuccess={() => {
            refetchSchedules();
            setScheduleFormModal(undefined);
          }}
        />
      )}

      {/* Payment Schedule Assign Modal */}
      {scheduleAssignModal && (
        <PaymentScheduleAssignModal
          schedule={scheduleAssignModal}
          onClose={() => setScheduleAssignModal(null)}
          onSuccess={() => {
            refetchSchedules();
            setScheduleAssignModal(null);
          }}
        />
      )}

      {/* Schedule Confirm Dialog */}
      {scheduleConfirmDialog && (
        <ConfirmDialog
          isOpen={scheduleConfirmDialog.isOpen}
          title={scheduleConfirmDialog.title}
          message={scheduleConfirmDialog.message}
          variant={scheduleConfirmDialog.variant}
          onConfirm={scheduleConfirmDialog.onConfirm}
          onCancel={() => setScheduleConfirmDialog(null)}
          isLoading={archiveScheduleMutation.isPending || restoreScheduleMutation.isPending || deleteScheduleMutation.isPending}
        />
      )}

      {/* Red-flag confirmation: settings change that wipes all tariffs */}
      {settingsResetConfirm && (
        <ConfirmDialog
          isOpen={true}
          title="⚠️ ATTENTION : remise à zéro de tous les tarifs"
          message={
            "Ce changement de structure tarifaire va REMETTRE À ZÉRO tous les tarifs configurés " +
            "(scolarité et frais d'inscription) pour TOUTES les filières de l'établissement. " +
            "Vous devrez ressaisir chaque tarif. Cette action est irréversible. Voulez-vous vraiment continuer ?"
          }
          confirmText="Oui, réinitialiser tous les tarifs"
          cancelText="Annuler"
          variant="danger"
          isLoading={saveSettingsMutation.isPending}
          onConfirm={async () => {
            try {
              await saveSettingsMutation.mutateAsync({ ...settingsResetConfirm, confirmReset: true });
              setSettingsResetConfirm(null);
              refetchGroupedGrids();
            } catch (err) {
              setSettingsResetConfirm(null);
              setActionError(err instanceof Error ? err.message : "Erreur lors de l'enregistrement des paramètres");
            }
          }}
          onCancel={() => setSettingsResetConfirm(null)}
        />
      )}

      {/* Reminder Form Modal */}
      {showReminderFormModal && (
        <ReminderFormModal
          reminder={selectedReminder}
          onClose={() => {
            setShowReminderFormModal(false);
            setSelectedReminder(null);
          }}
          onSuccess={() => {
            setShowReminderFormModal(false);
            setSelectedReminder(null);
          }}
        />
      )}
    </div>
  );
};
